import { useVirtualizer } from '@tanstack/react-virtual';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import css from './RecentChatsSection.module.scss';

/**
 * The single, virtualized scroll container for the chat history — Starred and
 * Recents (their headers included) live in ONE scrolling region and scroll
 * together. Rows are windowed with `@tanstack/react-virtual`, so long lists only
 * render their visible rows.
 *
 * Heterogeneous by design: the caller supplies `estimateSize` (headers and chat
 * rows have different fixed heights) and `renderItem`. The top/bottom fades are
 * driven by actual scroll state (a fade only shows when content is hidden in that
 * direction) — measured here since CSS-only scroll-fades can't do this in Firefox.
 */
export function ChatScrollList<T>({
  items,
  getKey,
  estimateSize,
  renderItem,
}: {
  items: T[];
  getKey: (item: T, index: number) => string | number;
  estimateSize: (index: number) => number;
  renderItem: (item: T, index: number) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => ref.current,
    estimateSize,
    // Small: the nav sidebar viewport only fits a handful of rows, so a large
    // overscan would mount most of a short list and defeat the windowing.
    overscan: 5,
  });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // setState with an unchanged boolean is a no-op for React, so this can't loop.
    setFadeTop(el.scrollTop > 1);
    setFadeBottom(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  // Coalesce scroll/resize bursts to one measurement per frame — each `measure`
  // forces a layout read (scrollHeight/clientHeight), so running it per raw scroll
  // event would reflow on every frame alongside the virtualizer's own work.
  const scheduleMeasure = useCallback(() => {
    if (rafRef.current !== null) return; // a frame is already pending
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure(); // initial state — one synchronous read is fine
    el.addEventListener('scroll', scheduleMeasure, { passive: true });
    // Catches viewport-size changes (window resize, drawer expand).
    const ro = new ResizeObserver(scheduleMeasure);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', scheduleMeasure);
      ro.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [measure, scheduleMeasure]);

  // Re-measure when the row count changes — the virtualized total height (and thus
  // scrollHeight) changes without the scroll box itself resizing.
  useEffect(measure, [measure, items.length]);

  const rows = rowVirtualizer.getVirtualItems();

  return (
    <div
      ref={ref}
      className={css.scroll}
      data-fade-top={fadeTop || undefined}
      data-fade-bottom={fadeBottom || undefined}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rows.map((vi) => {
          const item = items[vi.index];
          if (item === undefined) return null;
          return (
            <div
              key={getKey(item, vi.index)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${vi.size}px`,
                transform: `translateY(${vi.start}px)`,
              }}
            >
              {renderItem(item, vi.index)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
